import { useMemo, useState } from 'react';
import { N63_REFERENCE } from '../data/n63Reference';

const recordGroups = [
  {
    key: 'fase1',
    fase: '1',
    title: 'Fase 1 · Solicitud de información',
    records: [
      N63_REFERENCE.records.commonHeader,
      N63_REFERENCE.records.phase1Detail,
      N63_REFERENCE.records.commonFooter,
    ],
  },
  {
    key: 'fase2',
    fase: '2',
    title: 'Fase 2 · Respuesta bancaria',
    records: [
      N63_REFERENCE.records.commonHeader,
      N63_REFERENCE.records.phase2Detail,
      N63_REFERENCE.records.commonFooter,
    ],
  },
  {
    key: 'fase3',
    fase: '3',
    title: 'Fase 3 · Orden de embargo',
    records: [
      N63_REFERENCE.records.commonHeader,
      N63_REFERENCE.records.phase3Detail,
      N63_REFERENCE.records.phase3Complementary,
      N63_REFERENCE.records.commonFooter,
    ],
  },
];

function matchesField(field, query) {
  if (!query) return true;
  const haystack = [field.zona, field.pos, field.nombre, field.tipo, field.regla, field.descripcion]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return haystack.includes(query);
}

export default function AyudaN63() {
  const [faseActiva, setFaseActiva] = useState('1');
  const [search, setSearch] = useState('');

  const normalizedSearch = search.trim().toLowerCase();
  const activeGroup = recordGroups.find(group => group.fase === faseActiva) || recordGroups[0];

  const filteredRecords = useMemo(() => {
    return activeGroup.records
      .map(record => ({
        ...record,
        filteredFields: (record.fields || []).filter(field => matchesField(field, normalizedSearch)),
      }))
      .filter(record => !normalizedSearch || record.filteredFields.length > 0 || `${record.nombre} ${record.finalidad}`.toLowerCase().includes(normalizedSearch));
  }, [activeGroup, normalizedSearch]);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, marginBottom: 20 }}>
        <div>
          <h1 style={{ marginTop: 0, marginBottom: 8 }}>Ayuda técnica N63</h1>
          <p style={{ margin: 0, color: 'var(--color-text-muted)', maxWidth: 860 }}>
            Referencia amigable para revisar ficheros de <strong>Fase 1, Fase 2 y Fase 3</strong> sin salir de la app:
            tipos de registro, zonas, posiciones, longitudes, formato y puntos sensibles de validación.
          </p>
        </div>
        <div className="card" style={{ minWidth: 240, padding: 16 }}>
          <div style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Norma base</div>
          <div style={{ fontSize: '1.2rem', fontWeight: 700, marginTop: 4 }}>Cuaderno {N63_REFERENCE.meta.cuaderno} · {N63_REFERENCE.meta.version}</div>
          <div style={{ marginTop: 8, fontSize: '0.85rem' }}>{N63_REFERENCE.meta.recordLength} caracteres por registro</div>
          <div style={{ fontSize: '0.85rem' }}>{N63_REFERENCE.meta.encoding}</div>
          <div style={{ fontSize: '0.85rem' }}>Fin de línea: {N63_REFERENCE.meta.lineEnding}</div>
        </div>
      </div>

      <div className="grid-3" style={{ marginBottom: 20 }}>
        {N63_REFERENCE.overview.map(item => (
          <div key={item.fase} className="card n63-overview-card" style={{ padding: 18, border: item.fase === faseActiva ? '2px solid var(--color-brand-secondary)' : undefined }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
              <div>
                <div style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', textTransform: 'uppercase' }}>Fase {item.fase}</div>
                <h3 style={{ margin: '6px 0 0' }}>{item.nombre}</h3>
              </div>
              <button className={`btn btn-sm ${item.fase === faseActiva ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setFaseActiva(item.fase)}>
                Ver detalle
              </button>
            </div>
            <p style={{ color: 'var(--color-text-secondary)', fontSize: '0.9rem' }}>{item.objetivo}</p>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
              {item.usaRegistros.map(reg => (
                <span key={reg} className="badge badge-open">Tipo {reg}</span>
              ))}
            </div>
            <ul className="help-list">
              {item.notas.map(note => <li key={note}>{note}</li>)}
            </ul>
          </div>
        ))}
      </div>

      <div className="card" style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
          <div>
            <h2 style={{ margin: 0 }}>{activeGroup.title}</h2>
            <p style={{ margin: '6px 0 0', color: 'var(--color-text-muted)' }}>
              Filtra por zona, posición, nombre de campo, tipo o regla para revisar rápidamente un fichero real.
            </p>
          </div>
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Buscar: NRBE, 24-38, importe, IBAN, clave..."
            style={{ maxWidth: 340 }}
          />
        </div>
      </div>

      <div className="grid-2" style={{ marginBottom: 20 }}>
        <div className="card">
          <h3 style={{ marginTop: 0 }}>Checklist rápida de revisión</h3>
          <ul className="help-list compact">
            {N63_REFERENCE.commonChecks.map(item => <li key={item}>{item}</li>)}
          </ul>
        </div>
        <div className="card">
          <h3 style={{ marginTop: 0 }}>Dónde mirar primero si falla</h3>
          <div className="help-hints">
            {N63_REFERENCE.reviewHints.map(hint => (
              <div key={hint.titulo} className="help-hint-item">
                <div style={{ fontWeight: 700 }}>{hint.titulo}</div>
                <div style={{ color: 'var(--color-text-secondary)', fontSize: '0.9rem' }}>{hint.detalle}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {filteredRecords.map(record => (
        <div key={`${activeGroup.key}-${record.nombre}`} className="card" style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, marginBottom: 12, flexWrap: 'wrap' }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <h3 style={{ margin: 0 }}>{record.nombre}</h3>
                <span className="badge badge-progress">Tipo {record.codigo}</span>
              </div>
              <p style={{ margin: '6px 0 0', color: 'var(--color-text-secondary)' }}>{record.finalidad}</p>
              {record.base && <p style={{ margin: '6px 0 0', color: 'var(--color-text-muted)', fontSize: '0.85rem' }}>Base: {record.base}</p>}
            </div>
          </div>

          {record.notes?.length > 0 && (
            <div className="help-note-box">
              <ul className="help-list compact">
                {record.notes.map(note => <li key={note}>{note}</li>)}
              </ul>
            </div>
          )}

          {record.filteredFields?.length > 0 ? (
            <div className="help-table-wrap">
              <table className="data-table help-table">
                <thead>
                  <tr>
                    <th>Zona</th>
                    <th>Posición</th>
                    <th>Long.</th>
                    <th>Campo</th>
                    <th>Tipo</th>
                    <th>Regla / observación</th>
                  </tr>
                </thead>
                <tbody>
                  {record.filteredFields.map((field, idx) => (
                    <tr key={`${record.nombre}-${field.zona}-${idx}`}>
                      <td><strong>{field.zona}</strong></td>
                      <td>{field.pos}</td>
                      <td>{field.longitud}</td>
                      <td>
                        <div style={{ fontWeight: 600 }}>{field.nombre}</div>
                        {field.descripcion && <div style={{ fontSize: '0.82rem', color: 'var(--color-text-muted)' }}>{field.descripcion}</div>}
                      </td>
                      <td>{field.tipo}</td>
                      <td style={{ fontSize: '0.85rem' }}>{field.regla || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div style={{ color: 'var(--color-text-muted)', fontSize: '0.9rem' }}>No hay campos que coincidan con la búsqueda.</div>
          )}
        </div>
      ))}

      <div className="card" style={{ marginTop: 20 }}>
        <h2 style={{ marginTop: 0 }}>Cómo usar esta ayuda al revisar un fichero real</h2>
        <ol className="help-list ordered">
          <li>Comprueba primero que la primera línea sea tipo <strong>4</strong> y la última tipo <strong>8</strong>.</li>
          <li>Valida que cada línea mida exactamente <strong>650 caracteres</strong>.</li>
          <li>Busca aquí la zona conflictiva: por ejemplo <strong>NRBE</strong>, <strong>24-38</strong>, <strong>IBAN</strong>, <strong>importe</strong> o <strong>identificador deuda</strong>.</li>
          <li>Contrasta posición, longitud y relleno esperado con el fichero descargado desde la app.</li>
          <li>Si el banco rechaza solo uno de los ficheros, revisa diferencias entre entidades y fíjate especialmente en pie de fichero, fechas y campos a blancos.</li>
        </ol>
        <div className="help-note-box" style={{ marginTop: 12 }}>
          <strong>Nota de producto:</strong> esta ayuda está pensada como referencia operativa dentro del expediente; no sustituye la norma, pero evita tener que pelearse con el PDF vertical para revisar campos reales.
        </div>
      </div>
    </div>
  );
}
